import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BodyComponent } from './core/components/body/body.component';
import { SidenavComponent } from './core/components/sidenav/sidenav.component';
import { SublevelMenuComponent } from './core/components/sidenav/sublevel-menu/sublevel-menu.component';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { ActivityInterceptor } from './core/interceptors/activity.interceptor';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { provideRouteMap } from '@montra/route-map';
import { provideBatteryOverheat } from '@montra/battery-overheat';
import { PrimecustomModule } from './shared/primeng.custom.module';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { SharedModule } from './shared/shared.module';
import { AuthenticationService } from './shared/service/authentication.service';
import { HeaderComponent } from './core/components/header/header.component';
import { GlobalErrorHandlerService } from './core/services/global-error-handler.service';
import { AppStoreModule } from './store/store.module';
import { DialogService } from 'primeng/dynamicdialog';
import { SfAuthInterceptor } from './core/interceptors/SfAuth.Interceptor';
import { NotificationSideDrawerComponent } from './core/components/notification-side-drawer/notification-side-drawer.component';
import { BadgeModule } from 'primeng/badge';
import { UniversalFilterComponent } from './shared/component/filter/universal-filter/universal-filter.component';

import { HasPermissionDirective } from './shared/directive/has-permission.directive';
import { DraggableWidgetComponent } from './core/components/draggable-widget/draggable-widget.component';
import { DateFormatChangesInterceptor } from './core/interceptors/date-format-changes.interceptor';
import { VehicleSwitcherComponent } from './core/components/vehicle-switcher/vehicle-switcher.component';
import { BreadcrumbComponent } from './core/components/breadcrumb/breadcrumb.component';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeng/themes/lara';
import { definePreset } from '@primeng/themes';

const AppTheme = definePreset(Lara, {
  semantic: {
    primary: {
      50: '{blue.50}',
      100: '{blue.100}',
      200: '{blue.200}',
      300: '{blue.300}',
      400: '{blue.400}',
      500: '{blue.500}',
      600: '{blue.600}',
      700: '{blue.700}',
      800: '{blue.800}',
      900: '{blue.900}',
      950: '{blue.950}',
    },
  },
});

@NgModule({
  declarations: [
    AppComponent,
    BodyComponent,
    SidenavComponent,
    SublevelMenuComponent,
    HeaderComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    SharedModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    PrimecustomModule,
    ToastModule,
    AppStoreModule,
    NotificationSideDrawerComponent,
    BadgeModule,
    UniversalFilterComponent,
    HasPermissionDirective,
    DraggableWidgetComponent,
    VehicleSwitcherComponent,
    BreadcrumbComponent,
  ],
  providers: [
    { provide: 'API_URL', useValue: environment.apiBaseUrl },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SfAuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ActivityInterceptor,
      multi: true,
    },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DateFormatChangesInterceptor,
      multi: true,
    },
    AuthenticationService,
    MessageService,
    DialogService,
    provideHttpClient(withInterceptorsFromDi()),
    // Must be root-level: the library's services are providedIn 'root', so a
    // route-scoped provider is invisible to them. The library uses its own
    // HttpClient, so OnePortal's HTTP interceptors still don't see its calls.
    provideRouteMap({
      apiBaseUrl: environment.apiBaseUrl,
      googleMapsApiKey: environment.googleMapKey,
    }),
    // Same reason as provideRouteMap: the library's services are providedIn 'root'.
    // apiBaseUrl ends with '/', which the library needs (it appends 'report-service/...').
    provideBatteryOverheat({
      apiBaseUrl: environment.apiBaseUrl,
      googleMapsApiKey: environment.googleMapKey,
    }),
    providePrimeNG({
      theme: {
        preset: AppTheme,
        options: {
          // Forces PrimeNG to ONLY render the light mode variant of Lara
          darkModeSelector: 'none',
        },
      },
    }),
  ],
})
export class AppModule {}
